// @ts-nocheck

// App
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import PriceInput from 'app/shared/modules/MobileSRP/Header/FilterModules/PriceFilter/PriceInput';
import SliderThumb from 'app/shared/modules/CollectionResultPage/PriceFilter/SliderThumb';
import SliderTrack from 'app/shared/modules/CollectionResultPage/PriceFilter/SliderTrack';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import { PRICE_SLIDER_MAX } from 'app/shared/constants/FilterConstants';
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';
import {
  emitTrackHighPriceFilterEvent,
  emitTrackLowPriceFilterEvent,
} from 'app/shared/models/Clickstream/FilterClickstreamEvents';
import { HOMEPAGE_FILTER_AUTOMATION_IDS } from 'app/shared/modules/location/AutocompleteSearchInput/automation';

const HUNDRED_TO_ONE_SCALE = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;

export const blurFocusedElementWithin = (container) => {
  const activeElement = document.activeElement;
  if (container && activeElement instanceof HTMLElement && container.contains(activeElement)) {
    activeElement.blur();
  }
};

export const getPendingPriceStateFromContainer = ({ container, sliderValue }) => {
  const readInputValue = ({ selector, emptyValue, fallbackValue }) => {
    const input = container?.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) {
      return fallbackValue;
    }

    if (input.value === '') {
      return emptyValue;
    }

    const parsedValue = Number(input.value);
    if (Number.isNaN(parsedValue)) {
      return fallbackValue;
    }

    return Math.floor(parsedValue / HUNDRED_TO_ONE_SCALE);
  };

  const nextSliderValue = [
    readInputValue({
      selector: '#min-price-input',
      emptyValue: 0,
      fallbackValue: sliderValue[0],
    }),
    readInputValue({
      selector: '#max-price-input',
      emptyValue: PRICE_SLIDER_MAX,
      fallbackValue: sliderValue[1],
    }),
  ];

  return {
    sliderValue: nextSliderValue,
    priceState: {
      min: nextSliderValue[0] === 0 ? null : nextSliderValue[0] * HUNDRED_TO_ONE_SCALE,
      max: nextSliderValue[1] === PRICE_SLIDER_MAX ? null : nextSliderValue[1] * HUNDRED_TO_ONE_SCALE,
    },
  };
};

/**
 * Price slider - A component that contains display and business logic for the price filter
 * @param {Object} props - props object
 * @param {boolean} props.showResetButton [showResetButton=false] - Show or hide reset button
 * @param {boolean} props.preventDefault [preventDefault=false] - if true, only carry out Redux filter change (do not fecth listings)
 * @param {string} props.customTrackingEvent [customTrackingEvent=null] - if present, use event instead of default filter event
 * @returns component
 */
const PriceSlider = forwardRef(
  (
    {
      showResetButton = false,
      preventDefault = false,
      customTrackingEvent = null,
      triggerLocation = null,
      triggerObject = null,
    },
    ref,
  ) => {
    // Redux
    const price = useSelector((state) => state.filter.price);
    const containerRef = useRef(null);

    // React state
    const [priceState, setPriceState] = useState(price);
    const [sliderValue, setSliderValue] = useState([
      priceState.min ? priceState.min / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : 0,
      priceState.max ? priceState.max / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : PRICE_SLIDER_MAX,
    ]);

    // Hooks
    const { filterChange, reduxOnlyChange, resetFilter, isFilterResetting } = useFilterChange();

    const handlePriceChange = useCallback(
      (rawVals) => {
        const [min, max] = rawVals;
        const MULTIPLIER = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;
        const formattedVals = {
          min: min === 0 ? null : min * MULTIPLIER,
          max: max === PRICE_SLIDER_MAX ? null : max * MULTIPLIER,
        };
        setPriceState({ min: formattedVals.min, max: formattedVals.max });

        if (formattedVals.min !== priceState.min) {
          emitTrackLowPriceFilterEvent({ triggerLocation, triggerObject });
        } else if (formattedVals.max !== priceState.max) {
          emitTrackHighPriceFilterEvent({ triggerLocation, triggerObject });
        }
      },
      [priceState, setPriceState],
    );

    const handleSliderOnChange = useCallback((valArr) => {
      setSliderValue(valArr);
    }, []);

    const handleSliderOnAfterChange = useCallback(
      (valArr) => {
        handlePriceChange(valArr);
      },
      [handlePriceChange],
    );

    const handleSliderMinValueChange = useCallback(
      (val) => {
        const valArr = [val, sliderValue[1]];
        setSliderValue(valArr);
        handlePriceChange(valArr, true);
      },
      [sliderValue, handlePriceChange],
    );
    const handleSliderMaxValueChange = useCallback(
      (val) => {
        const valArr = [sliderValue[0], val];
        setSliderValue(valArr);
        handlePriceChange(valArr, true);
      },
      [sliderValue, handlePriceChange],
    );

    const ariaValueText = useCallback((state) => {
      return `Current ${state.index === 0 ? 'min' : 'max'} value: ${
        state.valueNow === 0 ? 'No minimum' : `${state.valueNow * VALUE_CONSTANTS.PRICE_FILTER_INCREMENT} dollars`
      }`;
    }, []);

    const handleResetClick = useCallback(() => {
      resetFilter({ price: DEFAULT.price, preventDefault });
      setPriceState(DEFAULT.price);
      setSliderValue([0, PRICE_SLIDER_MAX]);

      // resetFilter is not memoized in useFilterChange but only depends on dispatch (stable)
    }, []);

    const syncPendingInput = useCallback(() => {
      const pendingPrice = getPendingPriceStateFromContainer({
        container: containerRef.current,
        sliderValue,
      });
      const didSliderValueChange =
        pendingPrice.sliderValue[0] !== sliderValue[0] || pendingPrice.sliderValue[1] !== sliderValue[1];

      if (didSliderValueChange) {
        setSliderValue(pendingPrice.sliderValue);
        setPriceState(pendingPrice.priceState);

        if (preventDefault) {
          reduxOnlyChange({ price: pendingPrice.priceState });
        } else {
          filterChange({ price: pendingPrice.priceState });
        }
      }

      blurFocusedElementWithin(containerRef.current);
      return pendingPrice.priceState;

      // reduxOnlyChange/filterChange are stable dispatch wrappers from useFilterChange.
    }, [preventDefault, sliderValue]);

    useImperativeHandle(
      ref,
      () => ({
        handleInputBlur() {
          syncPendingInput();
        },
        syncPendingInput,
      }),
      [syncPendingInput],
    );

    // Listens for filter changes => updates Redux reducer
    useEffect(() => {
      if (!isFilterResetting) {
        if (preventDefault) {
          reduxOnlyChange({ price: priceState });
        } else {
          filterChange({ price: priceState });
        }
      }

      // reduxOnlyChange/filterChange not memoized in useFilterChange but only depend on dispatch (stable)
      // Including them causes infinite re-render loop. See HPWEB-6645
    }, [priceState, isFilterResetting, preventDefault]);

    return (
      <div ref={containerRef}>
        <GeneralFilterSection>
          <GeneralFilterHeader>
            <GeneralFilterLabel id="price-label">Price range</GeneralFilterLabel>
            {showResetButton && (
              <GeneralFilterResetButton onClick={handleResetClick} aria-label={'Reset all active price filters'}>
                Reset
              </GeneralFilterResetButton>
            )}
          </GeneralFilterHeader>

          <S.InputContainer>
            <PriceInput
              ariaLabel={'Set minimum price'}
              commitOnChange={preventDefault}
              data-name={preventDefault ? 'HomepagePriceMinInput' : undefined}
              data-testid={preventDefault ? HOMEPAGE_FILTER_AUTOMATION_IDS.priceMinInput : undefined}
              handleSliderValueChange={handleSliderMinValueChange}
              id={'min-price-input'}
              isMinInput
              name={'min-price-input'}
              placeholder={'No min'}
              value={sliderValue[0] * HUNDRED_TO_ONE_SCALE}
            />
            <S.RangeText aria-hidden="true">to</S.RangeText>
            <PriceInput
              ariaLabel={'Set maximum price'}
              commitOnChange={preventDefault}
              data-name={preventDefault ? 'HomepagePriceMaxInput' : undefined}
              data-testid={preventDefault ? HOMEPAGE_FILTER_AUTOMATION_IDS.priceMaxInput : undefined}
              handleSliderValueChange={handleSliderMaxValueChange}
              id={'max-price-input'}
              isMaxInput
              name={'max-price-input'}
              placeholder={'No max'}
              value={sliderValue[1] * HUNDRED_TO_ONE_SCALE}
            />
          </S.InputContainer>
          <S.PriceSlider
            aria-labelledby="price-info-card-label"
            ariaLabel={['Minimum price thumb', 'Maximum price thumb']}
            ariaValuetext={ariaValueText}
            min={0} // 1:100 scale
            max={PRICE_SLIDER_MAX} // 1:100 scale
            value={sliderValue} // 1:100 scale
            onChange={handleSliderOnChange}
            onAfterChange={handleSliderOnAfterChange}
            renderTrack={SliderTrack}
            renderThumb={SliderThumb}
          />
        </GeneralFilterSection>
      </div>
    );
  },
);

PriceSlider.displayName = 'PriceSlider';

export default PriceSlider;
